import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "../../../theme";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";

const AccordionSection = styled.section`
  box-sizing: border-box;
  margin: 0 0 0.5rem;
  padding: 0 1.5rem;
  background: ${colors.surface};
  border-radius: 12px;
  scroll-margin-top: 100px;
`;

const AccordionHeader = styled.button`
  all: unset;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  cursor: pointer;

  padding: 0.75rem 0;
  gap: 0.75rem;

  color: #111827;
  font-weight: 600;

  position: relative;
  z-index: 2;

  &:focus-visible {
    outline: 4px solid
      color-mix(in srgb, ${colors.linkHover.light} 50%, transparent);
    outline-offset: 2px;
  }

  @media (min-width: 1024px) {
    padding: 1rem 0;
    font-weight: 400;
  }
`;

const AccordionTitle = styled.span`
  font-size: 1.125rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ChevronIcon = styled.svg`
  width: 1.5rem;
  height: auto;
  flex-shrink: 0;
  margin-top: 0.25rem;
  fill: #000000;

  transition: transform 300ms ease, fill 150ms ease;

  ${({ $open }) =>
    $open &&
    `
      transform: rotate(-180deg);
    `}

  @media ${POINTER_HOVER_MEDIA} {
    ${AccordionHeader}:hover & {
      fill: ${colors.linkHover.dark};
    }
  }

  @media (min-width: 1024px) {
    width: 2rem;
    height: 2rem;
    margin-top: 0.125rem;
  }
`;

const AccordionContentOuter = styled.div`
  overflow: hidden;
  position: relative;
  z-index: 1;

  height: ${({ $height }) => `${$height}px`};
  transition: height 300ms ease-in-out;
`;

const AccordionContentInner = styled.div`
  padding-bottom: 1rem;
  color: #374151;

  @media (min-width: 1024px) {
    padding-bottom: 1.5rem;
  }
`;

const getHashId = () => {
  if (typeof window === "undefined") return "";
  return decodeURIComponent(window.location.hash.replace(/^#/, ""));
};

export default function Accordion({
  title,
  id,
  children,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [height, setHeight] = useState(0);
  const contentRef = useRef(null);
  const sectionRef = useRef(null);
  const pendingScrollId = useRef(null);
  const openRef = useRef(open);
  const heightRef = useRef(height);
  openRef.current = open;
  heightRef.current = height;

  useLayoutEffect(() => {
    if (!contentRef.current) return;

    if (open) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open, children]);

  // Open accordion when hash targets this section or a descendant
  useEffect(() => {
    const syncFromHash = () => {
      const hash = getHashId();
      if (!hash || !sectionRef.current) return;

      const target = document.getElementById(hash);
      const isSelf = Boolean(id && hash === id);
      const isChild = Boolean(target && sectionRef.current.contains(target));

      if (!isSelf && !isChild) return;

      // Already expanded - smooth-scroll immediately
      if (openRef.current && heightRef.current > 0) {
        window.requestAnimationFrame(() => {
          document
            .getElementById(hash)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return;
      }

      pendingScrollId.current = hash;
      setOpen(true);
    };

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    window.addEventListener("popstate", syncFromHash);
    return () => {
      window.removeEventListener("hashchange", syncFromHash);
      window.removeEventListener("popstate", syncFromHash);
    };
  }, [id]);

  // After expand animation, smoothly scroll to the hashed element
  useEffect(() => {
    if (!open || height <= 0 || !pendingScrollId.current) return;

    const hash = pendingScrollId.current;
    pendingScrollId.current = null;
    const outer = contentRef.current?.parentElement;

    const scrollToTarget = () => {
      const target = document.getElementById(hash);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Prefer waiting for the height transition so scroll starts after expand
    if (outer) {
      const onTransitionEnd = (event) => {
        if (event.propertyName !== "height") return;
        outer.removeEventListener("transitionend", onTransitionEnd);
        scrollToTarget();
      };
      outer.addEventListener("transitionend", onTransitionEnd);
      // Fallback if transitionend doesn't fire (already open / reduced motion)
      const fallback = window.setTimeout(() => {
        outer.removeEventListener("transitionend", onTransitionEnd);
        scrollToTarget();
      }, 400);
      return () => {
        outer.removeEventListener("transitionend", onTransitionEnd);
        window.clearTimeout(fallback);
      };
    }

    const timer = window.setTimeout(scrollToTarget, 320);
    return () => window.clearTimeout(timer);
  }, [open, height]);

  return (
    <AccordionSection
      ref={sectionRef}
      id={id}
      data-state={open ? "open" : "closed"}
    >
      <AccordionHeader
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id ? `${id}-content` : undefined}
      >
        <AccordionTitle>{title}</AccordionTitle>

        <ChevronIcon
          $open={open}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.3 15.0069L5.30005 9.00691L6.00716 8.2998L12.0072 14.2998L18.0072 8.2998L18.7143 9.00691L12.7143 15.0069L12.0072 15.714L11.3 15.0069Z"
          />
        </ChevronIcon>
      </AccordionHeader>

      <AccordionContentOuter
        id={id ? `${id}-content` : undefined}
        $height={height}
      >
        <AccordionContentInner ref={contentRef}>
          {children}
        </AccordionContentInner>
      </AccordionContentOuter>
    </AccordionSection>
  );
}
